import { Navigate, useLocation } from 'react-router-dom';
import { lazy, type ReactElement } from 'react';
import { useMe } from '@/lib/useMe';
import { resolveGuard, type GuardKind } from '@/lib/guards';
import { AppShell } from '@/components/shell/AppShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { InvitationPage } from '@/features/auth/InvitationPage';
import { PanouPage } from '@/features/panou/PanouPage';
import { UtilizarePage } from '@/features/utilizare/UtilizarePage';
import { MapariListPage } from '@/features/mapari/MapariListPage';
import { MaparePage } from '@/features/mapari/MaparePage';
import { NomenclatorPage } from '@/features/nomenclator/NomenclatorPage';

// Code-split the two routes that pull the heavy file libraries (ExcelJS +
// PapaParse, ~hundreds of KB): the XLSX/CSV upload parser (MapareNouaPage →
// lib/parse) and the export builder (ExportPage → lib/export). Lazy-loading
// keeps those libs out of the initial bundle, so the login/Panou landing
// screens the demo opens on don't download and parse them. Both render under
// AppShell's <Suspense> boundary.
const MapareNouaPage = lazy(() =>
  import('@/features/mapari/MapareNouaPage').then((m) => ({ default: m.MapareNouaPage })),
);
const ExportPage = lazy(() =>
  import('@/features/mapari/ExportPage').then((m) => ({ default: m.ExportPage })),
);
import { EchipaPage } from '@/features/echipa/EchipaPage';
import { GhidPage } from '@/features/ghid/GhidPage';
import { SetariPage } from '@/features/setari/SetariPage';
import { OperatorOrgsPage } from '@/features/operator/OperatorOrgsPage';

function Guard({ kind, children }: { kind: GuardKind; children: ReactElement }) {
  const { data: me, isPending } = useMe();
  const location = useLocation();
  if (isPending) return null; // brief; a skeleton lands with real pages in 3b+
  const res = resolveGuard(kind, me);
  if ('redirect' in res) return <Navigate to={res.redirect} replace state={{ from: location.pathname }} />;
  return children;
}

export const routes = [
  { path: '/login', element: <LoginPage /> },
  { path: '/invitatie/:token', element: <InvitationPage /> },
  {
    element: <Guard kind="auth"><AppShell /></Guard>,
    children: [
      { path: '/', element: <PanouPage /> },
      { path: '/mapari', element: <MapariListPage /> },
      { path: '/mapari/noua', element: <MapareNouaPage /> },
      { path: '/mapari/:id', element: <Navigate to="validare" replace /> },
      { path: '/mapari/:id/validare', element: <MaparePage /> },
      { path: '/mapari/:id/validare/:step', element: <MaparePage /> },
      { path: '/mapari/:id/tabel', element: <MaparePage /> },
      { path: '/mapari/:id/export', element: <ExportPage /> },
      { path: '/nomenclator', element: <NomenclatorPage /> },
      { path: '/echipa', element: <EchipaPage /> },
      { path: '/utilizare', element: <UtilizarePage /> },
      { path: '/ghid', element: <GhidPage /> },
      { path: '/setari', element: <SetariPage /> },
      { path: '/operator/organizatii', element: <Guard kind="operator"><OperatorOrgsPage /></Guard> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];
